// LandingPagePracto.jsx
import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import SiteFooter from "./LPFooter";
import DoctorListSEO from "./DoctorListSeo";
import "./LandingPage.css";
import HomeNursingBanner from "./HomeNurceBanners";
import {
  Search,
  MapPin,
  X,
  ShieldCheck,
  Star,
  Clock,
  Users,
  LogIn,
  UserPlus,
  Mail,
  Lock,
  Eye,
  EyeOff,
  AlertCircle,
  CheckCircle,
  Stethoscope,
  Building2,
} from "lucide-react";

import LogoMikDok from "./components/common/LogoMikDok";

import {
  collection,
  getDocs,
  doc,
  setDoc,
  getDoc,
  serverTimestamp,
  query,
  orderBy,
  where,
  limit,
} from "firebase/firestore";

import { ref, listAll, getDownloadURL } from "firebase/storage";
import { db, storage, auth } from "./firebase";

import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  setPersistence,
  browserLocalPersistence,
  sendPasswordResetEmail,
} from "firebase/auth";

/* ---- Policy versions ---- */
const TERMS_VERSION = "2025-11-01";
const PRIVACY_VERSION = "2025-11-01";

/* =========================
 * Translations
 * ========================= */
const translations = {
  en: {
    login: "Login",
    signUp: "Sign Up",
    heroTitle: "Find the right doctor.",
    heroSubtitle: "Search by specialty, name, or location. Book fast — no calls.",
    locationPlaceholder: "City (e.g., Tirana)",
    whatPlaceholder: "Specialty, service, symptom, doctor name…",
    clearAll: "Clear all",
    anySpecialty: "Any specialty",
    noResults: "No results. Try a different search.",
    verified: "Verified",
    nextAvailable: "Next available",
    fee: "Fee",
    book: "Book",
    viewProfile: "View profile",
    followers: "Followers",
    years: "yrs",
    welcomeBack: "Welcome back",
    createAccountTitle: "Create account",
    signInSubtitle: "Sign in to continue",
    iAmA: "I am a...",
    patient: "Patient",
    doctor: "Professional",
    clinic: "Clinic",
    emailPlaceholder: "you@example.com",
    passwordPlaceholder: "••••••••",
    confirmPassword: "Confirm password",
    processing: "Processing...",
    forgotPassword: "Forgot password?",
    resetEmailSent: "If this email is registered, we sent a password reset link.",
    enterEmailForReset: "Please enter your email to reset your password.",
    passwordResetError: "Could not send reset email. Please try again.",
    fillAllFields: "Please fill in all fields",
    passwordMismatch: "Passwords do not match",
    passwordRequirements: "Password does not meet requirements",
    mustAccept: "Please accept the Terms & Privacy to continue.",
    acceptTermsLabel: "I accept the Terms and Privacy Policy",
    marketingOptInLabel: "Send me product news & offers (optional).",
    registrationSuccess: "Registration successful! You can now sign in.",
    loginSuccess: "Login successful! Redirecting...",
    authFailed: "Authentication failed. Please try again.",
    noAccount: "No account found with this email address.",
    wrongPassword: "Incorrect password. Please try again.",
    invalidEmail: "Please enter a valid email address.",
    accountDisabled: "This account has been disabled. Please contact support.",
    tooManyAttempts: "Too many failed attempts. Please try again later.",
    profileNotFound: "User profile not found. Please contact support.",
    emailInUse: "An account with this email already exists.",
    weakPassword: "Password is too weak. Please choose a stronger password.",
    passwordMustContain: "Password must contain:",
    atLeast8Chars: "At least 8 characters",
    oneUppercase: "One uppercase letter",
    oneLowercase: "One lowercase letter",
    oneNumber: "One number",
    searchBtn: "Search",
    resultsTitle: "Results",
    provider: "provider",
    providers: "providers",
    mode: "Mode",
    allRights: "All rights reserved",
    privacy: "Privacy",
    terms: "Terms",
    contact: "Contact",
  },
  sq: {
    login: "Hyr",
    signUp: "Regjistrohu",
    heroTitle: "Gjej doktorin e duhur.",
    heroSubtitle: "Kërko sipas specialitetit, emrit ose vendndodhjes. Rezervo shpejt, pa telefonata.",
    locationPlaceholder: "Qyteti (p.sh., Tiranë)",
    whatPlaceholder: "Simptomë, specialitet, shërbim, emër…",
    clearAll: "Pastro",
    anySpecialty: "Çdo specialitet",
    noResults: "Nuk u gjetën rezultate. Provo një kërkim tjetër.",
    verified: "Verifikuar",
    nextAvailable: "Takimi më i afërt",
    fee: "Çmimi",
    book: "Rezervo",
    viewProfile: "Shiko profilin",
    followers: "Ndjekës",
    years: "vite",
    welcomeBack: "Mirë se u riktheve",
    createAccountTitle: "Krijo llogari",
    signInSubtitle: "Hyr për të vazhduar",
    iAmA: "Unë jam...",
    patient: "Pacient",
    doctor: "Profesionist",
    clinic: "Klinikë",
    emailPlaceholder: "ju@example.com",
    passwordPlaceholder: "••••••••",
    confirmPassword: "Konfirmo fjalëkalimin",
    processing: "Duke u përpunuar...",
    forgotPassword: "Keni harruar fjalëkalimin?",
    resetEmailSent: "Nëse ky email është i regjistruar, ju dërguam linkun e rivendosjes.",
    enterEmailForReset: "Vendos email-in për të rivendosur fjalëkalimin.",
    passwordResetError: "Nuk u dërgua email-i. Provo sërish.",
    fillAllFields: "Plotëso të gjitha fushat",
    passwordMismatch: "Fjalëkalimet nuk përputhen",
    passwordRequirements: "Fjalëkalimi nuk plotëson kërkesat",
    mustAccept: "Prano Kushtet & Privatësinë për të vazhduar.",
    acceptTermsLabel: "Pranoj Kushtet dhe Politikën e Privatësisë",
    marketingOptInLabel: "Më dërgoni lajme & oferta (opsionale).",
    registrationSuccess: "Regjistrimi u krye! Tani mund të hyni.",
    loginSuccess: "Hyrja u krye! Duke ridrejtuar...",
    authFailed: "Autentifikimi dështoi. Provo përsëri.",
    noAccount: "Nuk u gjet llogari me këtë email.",
    wrongPassword: "Fjalëkalim i gabuar.",
    invalidEmail: "Vendos një email të vlefshëm.",
    accountDisabled: "Kjo llogari është çaktivizuar.",
    tooManyAttempts: "Shumë përpjekje. Provo më vonë.",
    profileNotFound: "Profili nuk u gjet.",
    emailInUse: "Ekziston tashmë një llogari me këtë email.",
    weakPassword: "Fjalëkalim shumë i dobët.",
    passwordMustContain: "Fjalëkalimi duhet të përmbajë:",
    atLeast8Chars: "Të paktën 8 karaktere",
    oneUppercase: "Një shkronjë të madhe",
    oneLowercase: "Një shkronjë të vogël",
    oneNumber: "Një numër",
    searchBtn: "Kërko",
    resultsTitle: "Rezultatet",
    provider: "profesionist",
    providers: "profesionistë",
    mode: "Mënyra",
    allRights: "Të gjitha të drejtat e rezervuara",
    privacy: "Privatësia",
    terms: "Kushtet",
    contact: "Kontakt",
  },
};

/* =========================
 * Utils
 * ========================= */
const normalizeKeyword = (s) => (s ?? "").toString().trim().toLowerCase();
const joinText = (...parts) => parts.flat().filter(Boolean).map((x) => x.toString()).join(" ");

function useDebouncedValue(value, delay = 250) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

function useIsMobile(breakpoint = 820) {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= breakpoint);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [breakpoint]);
  return isMobile;
}

function useLocalStorage(key, initialValue) {
  const [value, setValue] = useState(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw != null ? JSON.parse(raw) : initialValue;
    } catch {
      return initialValue;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {}
  }, [key, value]);
  return [value, setValue];
}

const pad2 = (n) => n.toString().padStart(2, "0");

function weekdayKeyFromDate(d) {
  const keys = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
  return keys[d.getDay()];
}

function toMinutes(hhmm) {
  const [hh, mm] = (hhmm || "00:00").split(":").map(Number);
  return hh * 60 + mm;
}

function fromMinutes(mins) {
  const hh = Math.floor(mins / 60);
  const mm = mins % 60;
  return `${pad2(hh)}:${pad2(mm)}`;
}

function formatDateYMD(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function formatSlotLabel(dateYMD, timeHHMM) {
  const [yy, mm, dd] = dateYMD.split("-").map(Number);
  const dt = new Date(yy, (mm || 1) - 1, dd || 1);
  const day = dt.toLocaleDateString("en-US", { weekday: "short" });
  return `${day} ${timeHHMM}`;
}

function setOrCreateMeta(nameOrProp, value, isProperty = false) {
  if (!value) return;
  const selector = isProperty ? `meta[property="${nameOrProp}"]` : `meta[name="${nameOrProp}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    if (isProperty) el.setAttribute("property", nameOrProp);
    else el.setAttribute("name", nameOrProp);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}

function setCanonical(href) {
  if (!href) return;
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}

function setHreflangAlternates({ canonicalSq, canonicalEn }) {
  const upsert = (hreflang, href) => {
    if (!href) return;
    let link = document.head.querySelector(`link[rel="alternate"][hreflang="${hreflang}"]`);
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "alternate");
      link.setAttribute("hreflang", hreflang);
      document.head.appendChild(link);
    }
    link.setAttribute("href", href);
  };

  upsert("sq", canonicalSq);
  upsert("en", canonicalEn);
  upsert("x-default", canonicalSq || canonicalEn);
}

function upsertJsonLd(id, json) {
  const scriptId = `jsonld-${id}`;
  let el = document.getElementById(scriptId);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = scriptId;
    document.head.appendChild(el);
  }
  el.text = JSON.stringify(json);
}

/* =========================
 * Slug / Pretty URLs
 * ========================= */


/* =========================
 * Specialties (from Firestore)
 * ========================= */
async function fetchSpecialties() {
  const snap = await getDocs(query(collection(db, "specialties"), orderBy("updatedAt", "desc"), limit(300)));
  return snap.docs.map((d) => ({ key: d.id, ...d.data() }));
}

const getSpecLabel = (spec, lang) => {
  if (!spec) return "";
  if (typeof spec === "string") return spec;
  return spec?.labels?.[lang] || spec?.labels?.sq || spec?.labels?.en || spec?.key || "";
};

const normalizeSq = (s) =>
  (s || "")
    .toString()
    .trim()
    .toLowerCase()
    .replace(/ë/g, "e")
    .replace(/ç/g, "c");

/* Avatar */
const makeDoctorAvatar = (name) => {
  const initials = (name || "D")
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const colors = [
    { bg: "#3b82f6", text: "#FFF" },
    { bg: "#6366f1", text: "#FFF" },
    { bg: "#8b5cf6", text: "#FFF" },
    { bg: "#10b981", text: "#FFF" },
  ];
  const c = colors[(name || "").length % colors.length];

  return `data:image/svg+xml,${encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 200 200">
      <rect width="200" height="200" fill="${c.bg}"/>
      <text x="50%" y="50%" text-anchor="middle" dy=".35em" fill="${c.text}" font-size="80" font-family="Arial" font-weight="bold">
        ${initials}
      </text>
    </svg>
  `)}`;
};

async function normalizePhotoUrl(maybeUrlOrGsPath) {
  if (!maybeUrlOrGsPath) return null;
  if (maybeUrlOrGsPath.startsWith("http://") || maybeUrlOrGsPath.startsWith("https://")) return maybeUrlOrGsPath;
  try {
    const storageRefObj = ref(storage, maybeUrlOrGsPath);
    return await getDownloadURL(storageRefObj);
  } catch {
    return null;
  }
}

function guessCityFromLocation(loc) {
  const s = (typeof loc === "string" ? loc : loc?.address || "").toLowerCase();
  if (!s) return "";
  if (s.includes("tiran")) return "Tirana";
  if (s.includes("dur")) return "Durres";
  if (s.includes("vlore") || s.includes("vlor")) return "Vlore";
  if (s.includes("shkod")) return "Shkoder";
  if (s.includes("elbas")) return "Elbasan";
  if (s.includes("fier")) return "Fier";
  if (s.includes("korce") || s.includes("korç")) return "Korce";
  return "";
}

/* =========================
 * Availability logic
 * ========================= */
const BLOCKING_LOCK_STATUSES = new Set(["booked", "pending", "confirmed", "in-progress", "inprogress", "reserved"]);

function computeNext3AvailableFromSchedule({ workingHours, durationMinutes }, locksMap, now = new Date(), daysAhead = 21) {
  const dur = Math.max(5, Number(durationMinutes) || 30);
  const out = [];

  for (let i = 0; i <= daysAhead; i++) {
    const day = new Date(now);
    day.setDate(day.getDate() + i);

    const key = weekdayKeyFromDate(day);
    const wh = workingHours?.[key];
    if (!wh?.enabled) continue;

    const startMin = toMinutes(wh.start);
    const endMin = toMinutes(wh.end);

    const ymd = formatDateYMD(day);
    const lockedTimes = locksMap?.get(ymd) || new Set();

    const currentMin = i === 0 ? now.getHours() * 60 + now.getMinutes() : -1;

    for (let t = startMin; t + dur <= endMin; t += dur) {
      if (i === 0 && t <= currentMin) continue;

      const hhmm = fromMinutes(t);
      if (lockedTimes.has(hhmm)) continue;

      out.push({ date: ymd, time: hhmm, label: formatSlotLabel(ymd, hhmm) });
      if (out.length >= 3) return out;
    }
  }

  return out;
}

async function fetchDoctorLocksMap(doctorId, daysAhead = 21) {
  const start = new Date();
  const end = new Date();
  end.setDate(end.getDate() + daysAhead);

  const startYMD = formatDateYMD(start);
  const endYMD = formatDateYMD(end);

  const qy = query(
    collection(db, "locks"),
    where("doctorId", "==", doctorId),
    where("scheduledDate", ">=", startYMD),
    where("scheduledDate", "<=", endYMD),
    limit(500)
  );

  const snap = await getDocs(qy);

  const map = new Map();
  snap.forEach((docu) => {
    const x = docu.data();
    const day = x.scheduledDate;
    const time = x.scheduledTime;
    const status = (x.status || "").toString().toLowerCase();

    if (!day || !time) return;
    if (status && !BLOCKING_LOCK_STATUSES.has(status)) return;

    if (!map.has(day)) map.set(day, new Set());
    map.get(day).add(time);
  });

  return map;
}

/* =========================
 * useDoctorNextAvailability (HOOK)
 * ========================= */
const __availCache = new Map(); // doctorId -> { slots, ts }
const __pending = new Map(); // doctorId -> promise

function useDoctorNextAvailability({ doctorId, workingHours, durationMinutes, daysAhead = 21 }) {
  const [slots, setSlots] = useState(() => {
    const cached = __availCache.get(doctorId);
    if (cached?.slots?.length) return cached.slots;

    // placeholder from schedule only (no locks)
    return computeNext3AvailableFromSchedule({ workingHours, durationMinutes }, new Map(), new Date(), daysAhead);
  });

  useEffect(() => {
    let cancelled = false;
    if (!doctorId) return;

    const cached = __availCache.get(doctorId);
    if (cached?.slots?.length) {
      setSlots(cached.slots);
      return;
    }

    if (__pending.has(doctorId)) {
      __pending.get(doctorId).then((res) => {
        if (!cancelled && res?.length) setSlots(res);
      });
      return () => {
        cancelled = true;
      };
    }

    const p = (async () => {
      try {
        const locksMap = await fetchDoctorLocksMap(doctorId, daysAhead);
        const real = computeNext3AvailableFromSchedule({ workingHours, durationMinutes }, locksMap, new Date(), daysAhead);
        __availCache.set(doctorId, { slots: real, ts: Date.now() });
        return real;
      } catch {
        return null;
      } finally {
        __pending.delete(doctorId);
      }
    })();

    __pending.set(doctorId, p);

    p.then((res) => {
      if (!cancelled && res?.length) setSlots(res);
    });

    return () => {
      cancelled = true;
    };
  }, [doctorId, workingHours, durationMinutes, daysAhead]);

  return slots;
}

/* =========================
 * Auth Sheet
 * ========================= */
function AuthSheet({ isLogin, onClose, onSwitch, t, navigate }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [role, setRole] = useState("patient");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const emailRef = useRef(null);

  useEffect(() => emailRef.current?.focus(), []);

  useEffect(() => {
    const off = onAuthStateChanged(auth, async (u) => {
      if (!u) return;
      try {
        const snap = await getDoc(doc(db, "users", u.uid));
        if (!snap.exists()) return;
        const d = snap.data();
        const complete = d.profileComplete ?? false;
        const role = d.role;

        if (role === "doctor") navigate(complete ? "/dashboard-doctor" : "/doctor-profile-quick", { replace: true });
        else if (role === "patient") navigate(complete ? "/dashboard-patient" : "/patient-setup", { replace: true });
        else if (role === "clinic") navigate(complete ? "/dashboard-clinic" : "/clinic-profile-setup", { replace: true });
        else if (role === "admin") navigate("/admin-dashboard", { replace: true });
      } catch {}
    });
    return () => off();
  }, [navigate]);

  const validatePassword = (pwd) => ({
    minLength: pwd.length >= 8,
    hasUpper: /[A-Z]/.test(pwd),
    hasLower: /[a-z]/.test(pwd),
    hasNumber: /\d/.test(pwd),
  });
  const ok = validatePassword(password);

  const handleResetPassword = async () => {
    if (!email) {
      setError(t.enterEmailForReset);
      return;
    }
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      await sendPasswordResetEmail(auth, email);
      setSuccess(t.resetEmailSent);
    } catch {
      setError(t.passwordResetError || t.authFailed);
    } finally {
      setLoading(false);
    }
  };

  const submit = async () => {
    if (!email || !password || (!isLogin && !confirmPassword)) {
      setError(t.fillAllFields);
      return;
    }
    if (!isLogin && password !== confirmPassword) {
      setError(t.passwordMismatch);
      return;
    }
    if (!isLogin && !(ok.minLength && ok.hasUpper && ok.hasLower && ok.hasNumber)) {
      setError(t.passwordRequirements);
      return;
    }
    if (!isLogin && !acceptTerms) {
      setError(t.mustAccept);
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      if (isLogin) {
        await setPersistence(auth, browserLocalPersistence);
        const cred = await signInWithEmailAndPassword(auth, email, password);
        const snap = await getDoc(doc(db, "users", cred.user.uid));
        if (!snap.exists()) throw new Error(t.profileNotFound);
        setSuccess(t.loginSuccess);
        setTimeout(() => onClose?.(), 700);
      } else {
        const cred = await createUserWithEmailAndPassword(auth, email, password);
        await setDoc(doc(db, "users", cred.user.uid), {
          email,
          role,
          createdAt: serverTimestamp(),
          profileComplete: false,
          consent: {
            acceptedTerms: true,
            termsVersion: TERMS_VERSION,
            privacyVersion: PRIVACY_VERSION,
            consentAt: serverTimestamp(),
            marketingOptIn,
          },
        });
        setSuccess(t.registrationSuccess);
        setTimeout(() => {
          onSwitch?.();
          setPassword("");
          setConfirmPassword("");
          setSuccess("");
        }, 900);
      }
    } catch (err) {
      const code = err?.code;
      let msg = t.authFailed;
      if (code === "auth/user-not-found") msg = t.noAccount;
      else if (code === "auth/wrong-password") msg = t.wrongPassword;
      else if (code === "auth/invalid-email") msg = t.invalidEmail;
      else if (code === "auth/user-disabled") msg = t.accountDisabled;
      else if (code === "auth/too-many-requests") msg = t.tooManyAttempts;
      else if (code === "auth/email-already-in-use") msg = t.emailInUse;
      else if (code === "auth/weak-password") msg = t.weakPassword;
      else if (err?.message) msg = err.message;
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="mdk-sheet-backdrop" onClick={onClose} />
      <section className="mdk-sheet" role="dialog" aria-modal="true">
        <header className="mdk-sheet-h">
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div className="mdk-badge" style={{ width: 36, height: 36, borderRadius: 12 }}>
              <LogoMikDok showText={false} subtitle="" badge={56} size={34} markColor="#ffffff" />
            </div>
            <div style={{ display: "grid" }}>
              <strong style={{ fontSize: 16 }}>{isLogin ? t.welcomeBack : t.createAccountTitle}</strong>
              <span style={{ fontSize: 12, fontWeight: 900, color: "rgba(11,15,24,.55)" }}>
                {isLogin ? t.signInSubtitle : t.iAmA}
              </span>
            </div>
          </div>

          <button className="mdk-sheet-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        <div className="mdk-sheet-b">
          {error && (
            <div className="mdk-msg err">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}
          {success && (
            <div className="mdk-msg ok">
              <CheckCircle size={16} />
              <span>{success}</span>
            </div>
          )}

          {!isLogin && (
            <div style={{ display: "grid", gap: 8, marginBottom: 12 }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
                {[
                  { value: "patient", label: t.patient, icon: Users },
                  { value: "doctor", label: t.doctor, icon: Stethoscope },
                  { value: "clinic", label: t.clinic, icon: Building2 },
                ].map((opt) => {
                  const Icon = opt.icon;
                  const active = role === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      className="mdk-btn"
                      style={{
                        background: active ? "rgba(99,102,241,.10)" : "rgba(255,255,255,.85)",
                        borderColor: active ? "rgba(99,102,241,.35)" : "rgba(15,23,42,.10)",
                        justifyContent: "center",
                      }}
                      onClick={() => setRole(opt.value)}
                      aria-pressed={active}
                    >
                      <Icon size={16} />
                      <span>{opt.label}</span>
                    </button>
                  );
                })}
              </div>

              <label style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 13, fontWeight: 900 }}>
                <input
                  type="checkbox"
                  checked={acceptTerms}
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                  style={{ marginTop: 3 }}
                />
                <span style={{ color: "rgba(11,15,24,.86)" }}>
                  {t.acceptTermsLabel}{" "}
                  <a href="/terms" style={{ color: "rgb(99,102,241)", fontWeight: 1000 }}>
                    Terms
                  </a>{" "}
                  &{" "}
                  <a href="/privacy" style={{ color: "rgb(99,102,241)", fontWeight: 1000 }}>
                    Privacy
                  </a>
                </span>
              </label>

              <label style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 13, fontWeight: 900 }}>
                <input
                  type="checkbox"
                  checked={marketingOptIn}
                  onChange={(e) => setMarketingOptIn(e.target.checked)}
                  style={{ marginTop: 3 }}
                />
                <span style={{ color: "rgba(11,15,24,.55)" }}>{t.marketingOptInLabel}</span>
              </label>
            </div>
          )}

          <div className="mdk-field2">
            <div style={{ position: "relative" }}>
              <Mail size={16} style={{ position: "absolute", left: 12, top: 14, color: "rgba(11,15,24,.35)" }} />
              <input
                ref={emailRef}
                className="mdk-input"
                style={{ paddingLeft: 38 }}
                type="email"
                placeholder={t.emailPlaceholder}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div style={{ position: "relative" }}>
              <Lock size={16} style={{ position: "absolute", left: 12, top: 14, color: "rgba(11,15,24,.35)" }} />
              <input
                className="mdk-input"
                style={{ paddingLeft: 38, paddingRight: 44 }}
                type={showPassword ? "text" : "password"}
                placeholder={t.passwordPlaceholder}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                style={{
                  position: "absolute",
                  right: 8,
                  top: 6,
                  border: "none",
                  background: "transparent",
                  padding: 8,
                  cursor: "pointer",
                  color: "rgba(11,15,24,.35)",
                }}
                aria-label={showPassword ? "Hide" : "Show"}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            {isLogin && (
              <button
                type="button"
                onClick={handleResetPassword}
                style={{
                  border: "none",
                  background: "transparent",
                  color: "rgb(99,102,241)",
                  fontWeight: 1000,
                  cursor: "pointer",
                  justifySelf: "start",
                  padding: 0,
                }}
              >
                {t.forgotPassword}
              </button>
            )}

            {!isLogin && (
              <>
                <div style={{ position: "relative" }}>
                  <Lock size={16} style={{ position: "absolute", left: 12, top: 14, color: "rgba(11,15,24,.35)" }} />
                  <input
                    className="mdk-input"
                    style={{ paddingLeft: 38, paddingRight: 44 }}
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder={t.confirmPassword}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword((s) => !s)}
                    style={{
                      position: "absolute",
                      right: 8,
                      top: 6,
                      border: "none",
                      background: "transparent",
                      padding: 8,
                      cursor: "pointer",
                      color: "rgba(11,15,24,.35)",
                    }}
                    aria-label={showConfirmPassword ? "Hide" : "Show"}
                  >
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>

                {password && (
                  <div style={{ fontSize: 12, fontWeight: 900, color: "rgba(11,15,24,.55)", display: "grid", gap: 4 }}>
                    <span style={{ color: "rgba(11,15,24,.82)" }}>{t.passwordMustContain}</span>
                    <span style={{ color: ok.minLength ? "#16a34a" : "#b91c1c" }}>• {t.atLeast8Chars}</span>
                    <span style={{ color: ok.hasUpper ? "#16a34a" : "#b91c1c" }}>• {t.oneUppercase}</span>
                    <span style={{ color: ok.hasLower ? "#16a34a" : "#b91c1c" }}>• {t.oneLowercase}</span>
                    <span style={{ color: ok.hasNumber ? "#16a34a" : "#b91c1c" }}>• {t.oneNumber}</span>
                  </div>
                )}
              </>
            )}

            <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
              <button className="mdk-btn" type="button" onClick={onSwitch} disabled={loading} style={{ flex: 1 }}>
                {isLogin ? t.signUp : t.login}
              </button>
              <button className="mdk-btn primary" type="button" onClick={submit} disabled={loading} style={{ flex: 1 }}>
                {loading ? t.processing : isLogin ? t.login : t.signUp}
              </button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

/* =========================
 * Doctor Row
 * ========================= */
function DoctorRow({ doctor, t, onBook, onView }) {
  const name = doctor.name || "Doctor";
  const verified = doctor.verified === true;
  const rating = Number.isFinite(doctor.rating) ? doctor.rating.toFixed(1) : "4.5";
  const fee = doctor.feeText || "—";

  const fallback = useMemo(() => makeDoctorAvatar(name), [name]);
  const [imgSrc, setImgSrc] = useState(doctor.photoURL || fallback);

  const nextSlots = useDoctorNextAvailability({
    doctorId: doctor.id,
    workingHours: doctor.workingHours,
    durationMinutes: doctor.durationMinutes,
    daysAhead: 21,
  });

  const first = nextSlots?.[0]?.label || "—";
  const extraCount = Math.max(0, (nextSlots?.length || 0) - 1);

  return (
    <article className="mdk-row" aria-label={name}>
      <div className="mdk-photo">
        <img src={imgSrc} alt={name} loading="lazy" onError={() => setImgSrc(fallback)} />
      </div>

      <div>
        <h3 className="mdk-name">
          {name}
          {verified && (
            <span className="mdk-verified" title={t.verified}>
              <ShieldCheck size={14} />
              {t.verified}
            </span>
          )}
        </h3>

        <div className="mdk-meta">
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Star size={14} color="#f59e0b" />
            <b>{rating}</b>
          </span>

          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Stethoscope size={14} />
            <b>{doctor.specialtyLabel || doctor.specialtyKey || "General"}</b>
          </span>

          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <MapPin size={14} />
            <span>{doctor.city || doctor.location || "—"}</span>
          </span>

          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Users size={14} />
            <b>{doctor.followerCount || 0}</b> {t.followers}
          </span>

          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Clock size={14} />
            <b>{doctor.experience || "5+"}</b> {t.years}
          </span>
        </div>

        <div className="mdk-tags">
          <span className="mdk-tag">
            {t.nextAvailable}: <b style={{ marginLeft: 6 }}>{first}</b>
            {extraCount > 0 ? <span style={{ marginLeft: 8, opacity: 0.7 }}>+{extraCount}</span> : null}
          </span>

          <span className="mdk-tag">
            {t.fee}: <b style={{ marginLeft: 6 }}>{fee}</b>
          </span>

          {doctor.mode && (
            <span className="mdk-tag">
              {t.mode}: <b style={{ marginLeft: 6 }}>{doctor.mode}</b>
            </span>
          )}
        </div>
      </div>

      <div className="mdk-row-actions">
        <button className="mdk-cta primary" onClick={onBook} type="button">
          {t.book}
        </button>
        <button className="mdk-cta secondary" onClick={onView} type="button">
          {t.viewProfile}
        </button>
      </div>
    </article>
  );
}

/* =========================
 * Main Page
 * ========================= */
// put OUTSIDE the component (top of file)
const slugifyPathPart = (s) =>
  (s || "")
    .toString()
    .trim()
    .replace(/ë/g, "e")
    .replace(/ç/g, "c")
    .replace(/Ë/g, "E")
    .replace(/Ç/g, "C")
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .toLowerCase();

const buildPrettyCanonical = (baseDomain, { lang, city, specKey }) => {
  const c = slugifyPathPart(city);
  const s = (specKey || "").toString().trim();
  if (!c || !s) return `${baseDomain}/${lang}/`;
  return `${baseDomain}/${lang}/${encodeURIComponent(c)}/${encodeURIComponent(s)}`;
};


export default function LandingPagePracto() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [language, setLanguage] = useLocalStorage("language", "sq");
  const t = translations[language];

  const [specialties, setSpecialties] = useState([]);
  const [specLoading, setSpecLoading] = useState(true);

  const [sp, setSp] = useSearchParams();

  const cityParam = sp.get("city") || "";
  const qParam = sp.get("q") || "";
  const specParam = sp.get("spec") || ""; // MUST be specialtyKey
  const langParam = sp.get("lang") || "";

  const [city, setCity] = useState(cityParam);
  const [what, setWhat] = useState(qParam);
  const debouncedWhat = useDebouncedValue(what, 200);

  const [showSug, setShowSug] = useState(false);
  const [activeSug, setActiveSug] = useState(0);
  const sugRef = useRef(null);

  const [showAuth, setShowAuth] = useState(false);
  const [isLogin, setIsLogin] = useState(true);

  const WHATSAPP_HOME_NURSING = "355698077771"; // put your WhatsApp number (no +)
const waLink = (text) =>
  `https://wa.me/${WHATSAPP_HOME_NURSING}?text=${encodeURIComponent(text)}`;
 const nursingSlides = [
    { src: "/banners/home-nursing-1.mp4", alt: "Nurse", label: language === "sq" ? "Kujdes në shtëpi" : "At-home care" },
    { src: "/banners/home-nursing-3.jpg", alt: "IV", label: language === "sq" ? "Serum" : "IV" },
    { src: "/banners/home-nursing-2.jpg", alt: "Vitals", label: language === "sq" ? "Tension" : "Vitals" },
  ];

  const setParam = useCallback(
    (key, val) => {
      const next = new URLSearchParams(sp);
      if (!val) next.delete(key);
      else next.set(key, val);
      setSp(next, { replace: true });
    },
    [sp, setSp]
  );


useEffect(() => {
  let alive = true;
  (async () => {
    try {
      setSpecLoading(true);
      const list = await fetchSpecialties();
      if (!alive) return;
      setSpecialties(
        (list || [])
          .map((x) => ({
            key: x.key || x.id,
            labels: x.labels || {},
            synonymsSq: Array.isArray(x.synonymsSq) ? x.synonymsSq : [],
            synonymsEn: Array.isArray(x.synonymsEn) ? x.synonymsEn : [],
            updatedAt: x.updatedAt || null,
          }))
          .filter((x) => !!x.key)
      );
    } catch {
      if (alive) setSpecialties([]);
    } finally {
      if (alive) setSpecLoading(false);
    }
  })();
  return () => {
    alive = false;
  };
}, []);


  useEffect(() => setCity(cityParam), [cityParam]);
  useEffect(() => setWhat(qParam), [qParam]);
  useEffect(() => setParam("lang", language), [language, setParam]);

  // URL lang wins
  useEffect(() => {
    const lp = (langParam || "").toLowerCase();
    if (lp === "sq" || lp === "en") {
      if (language !== lp) setLanguage(lp);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [langParam]);

  // Load specialties once
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setSpecLoading(true);
        const list = await fetchSpecialties();
        if (!alive) return;
        setSpecialties(
          (list || [])
            .map((x) => ({
              key: x.key || x.id,
              labels: x.labels || {},
              synonymsSq: Array.isArray(x.synonymsSq) ? x.synonymsSq : [],
              updatedAt: x.updatedAt || null,
            }))
            .filter((x) => !!x.key)
        );
      } catch {
        if (alive) setSpecialties([]);
      } finally {
        if (alive) setSpecLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) return;
      try {
        const snap = await getDoc(doc(db, "users", u.uid));
        if (!snap.exists()) return;
        const d = snap.data();
        const role = d.role;
        const complete = d.profileComplete ?? false;

        if (role === "doctor") navigate(complete ? "/dashboard-doctor" : "/doctor-profile-setup", { replace: true });
        else if (role === "patient") navigate(complete ? "/dashboard-patient" : "/patient-setup", { replace: true });
        else if (role === "clinic") navigate(complete ? "/dashboard-clinic" : "/clinic-profile-setup", { replace: true });
        else if (role === "admin") navigate("/admin-dashboard", { replace: true });
      } catch {}
    });
    return () => unsub();
  }, [navigate]);

  // Flag + language toggle
  const languageFlag = language === "sq" ? "🇦🇱" : "🇬🇧";
  const toggleLanguage = () => {
    setLanguage((p) => {
      const next = p === "en" ? "sq" : "en";
      setParam("lang", next);
      return next;
    });
  };

  const [selectedSpec, setSelectedSpec] = useState(specParam);
  useEffect(() => setSelectedSpec(specParam), [specParam]);

  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchDoctorPhoto = useCallback(async (doctorId, doctorInfo) => {
    const inlinePhoto = doctorInfo?.photoURL || doctorInfo?.photoUrl || doctorInfo?.profilePicture;
    const normalized = await normalizePhotoUrl(inlinePhoto);
    if (normalized) return normalized;

    try {
      const folderRef = ref(storage, `doctor_photos/${doctorId}`);
      const files = await listAll(folderRef);
      const img = files.items.find((it) => /\.(jpg|jpeg|png|webp|gif)$/i.test(it.name));
      if (img) return await getDownloadURL(img);
    } catch {}
    return makeDoctorAvatar(doctorInfo?.name || "Doctor");
  }, []);

  const fetchDoctors = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      let qy;
if (selectedSpec) {
  qy = query(
    collection(db, "doctors"),
    where("specialtyKey", "==", selectedSpec),
    limit(250)
  );
} else {
  qy = query(collection(db, "doctors"), orderBy("rankScore", "desc"), limit(250));
}


      const snap = await getDocs(qy);
      const raw = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      const resolved = await Promise.all(
        raw.map(async (info) => {
          const serviceTitles = (info.services || []).map((s) => s?.title || "").filter(Boolean);

          const keywords = Array.from(
            new Set(
              (info.services || [])
                .flatMap((s) => (Array.isArray(s?.keywords) ? s.keywords : []))
                .map(normalizeKeyword)
                .filter(Boolean)
            )
          );

          const baseName = info.name || "Unknown Doctor";

          const specialtyKey = info.specialtyKey || slugifyPathPart(info.specialty || info.specialtySq || "");
          const specialtyLabel =
            (language === "sq" ? info.specialtySq : info.specialtyEn) ||
            info.specialty ||
            specialtyKey ||
            "General Practice";

          const address = info.location?.address || info.location || "";
          const bio = info.bio || "";
          const cityGuess = info.city || guessCityFromLocation(address);

          const searchCorpus = joinText(baseName, specialtyLabel, specialtyKey, address, cityGuess, bio, serviceTitles, keywords).toLowerCase();
          const durationMinutes = Number(info?.services?.[0]?.durationMinutes ?? info?.durationMinutes ?? 30) || 30;

          return {
            id: info.id,
            name: baseName,
            specialtyKey,
            specialtyLabel,
            rating: parseFloat(((info.rating ?? 4.5)).toFixed(1)),
            location: address || "—",
            city: cityGuess || "",
            photoURL: await fetchDoctorPhoto(info.id, info),
            followerCount: info.followerCount || 0,
            bio,
            experience: (info.experienceYears ?? info.experience ?? "5+").toString().replace("+ years", "+"),
            verified: info.isVerified === true || info.verified === true,
            rankScore: typeof info.rankScore === "number" ? info.rankScore : 0,
            feeText: info.feeText || info.priceText || info.consultFee || "",
            mode: info.mode || "",
            keywords,
            topKeywords: keywords.slice(0, 5),
            searchCorpus,
            workingHours: info.workingHours || {},
            durationMinutes,
          };
        })
      );

      setDoctors(resolved);
    } catch (e) {
      setError(e?.message || "Failed to load doctors");
      setDoctors([]);
    } finally {
      setLoading(false);
    }
  }, [fetchDoctorPhoto, selectedSpec, language]);

  useEffect(() => {
    fetchDoctors();
  }, [fetchDoctors]);

  const specialtyCounts = useMemo(() => {
    const counts = new Map();
    doctors.forEach((d) => {
      const k = d.specialtyKey || "";
      if (!k) return;
      counts.set(k, (counts.get(k) || 0) + 1);
    });

    const list = (specialties || []).map((s) => {
      const key = s.key;
      const count = counts.get(key) || 0;
      const label = getSpecLabel(s, language);
      return { key, label, count };
    });

    doctors.forEach((d) => {
      const k = d.specialtyKey || "";
      if (!k) return;
      const exists = list.some((x) => x.key === k);
      if (!exists) list.push({ key: k, label: d.specialtyLabel || k, count: counts.get(k) || 0 });
    });

    return list.sort((a, b) => (a.label || "").localeCompare(b.label || ""));
  }, [doctors, specialties, language]);

  const symptomPool = useMemo(() => {
    const freq = new Map();
    doctors.forEach((d) => {
      (Array.isArray(d.keywords) ? d.keywords : []).forEach((k) => {
        const key = normalizeKeyword(k);
        if (!key) return;
        freq.set(key, (freq.get(key) || 0) + 1);
      });
    });

    return Array.from(freq.entries())
      .sort((a, b) => b[1] - a[1] || a[0].length - b[0].length)
      .map(([k]) => k);
  }, [doctors]);

  const suggestions = useMemo(() => {
    const q = (debouncedWhat || "").trim().toLowerCase();
    const mk = (obj) => obj;

    if (!q) {
      const topSpecs = specialtyCounts.slice(0, 8).map((s) =>
        mk({
          type: "specialty",
          label: language === "sq" ? "Specialitet" : "Specialty",
          value: s.label,
          meta: `${s.count}`,
          specKey: s.key,
        })
      );

      const topSyms = symptomPool.slice(0, 8).map((s) =>
        mk({ type: "symptom", label: language === "sq" ? "Simptomë/Shërbim" : "Symptom/Service", value: s })
      );
      return [...topSpecs, ...topSyms].slice(0, 12);
    }

    const specHits = specialtyCounts
      .filter((s) => normalizeSq(s.label).includes(normalizeSq(q)) || normalizeSq(s.key).includes(normalizeSq(q)))
      .slice(0, 8)
      .map((s) =>
        mk({
          type: "specialty",
          label: language === "sq" ? "Specialitet" : "Specialty",
          value: s.label,
          meta: `${s.count}`,
          specKey: s.key,
        })
      );

    const symHits = symptomPool
      .filter((s) => s.includes(q))
      .sort((a, b) => {
        const aStarts = a.startsWith(q) ? 1 : 0;
        const bStarts = b.startsWith(q) ? 1 : 0;
        if (aStarts !== bStarts) return bStarts - aStarts;
        return a.length - b.length;
      })
      .slice(0, 8)
      .map((s) => mk({ type: "symptom", label: language === "sq" ? "Simptomë/Shërbim" : "Symptom/Service", value: s }));

    const docHits = doctors
      .filter((d) => (d.name || "").toLowerCase().includes(q))
      .slice(0, 6)
      .map((d) =>
        mk({
          type: "doctor",
          label: language === "sq" ? "Doktor" : "Doctor",
          value: d.name,
          doctorId: d.id,
        })
      );

    const out = [...specHits, ...symHits, ...docHits];
    const seen = new Set();
    return out
      .filter((x) => {
        const key = `${x.type}:${x.value}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .slice(0, 12);
  }, [debouncedWhat, specialtyCounts, symptomPool, doctors, language]);

  useEffect(() => {
    const onDown = (e) => {
      if (!sugRef.current) return;
      if (!sugRef.current.contains(e.target)) setShowSug(false);
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, []);

  useEffect(() => setParam("city", city.trim() ? city.trim() : ""), [city, setParam]);
  useEffect(() => setParam("q", what.trim() ? what.trim() : ""), [what, setParam]);
  useEffect(() => setParam("spec", selectedSpec || ""), [selectedSpec, setParam]);

  const filteredDoctors = useMemo(() => {
    const q = debouncedWhat.trim().toLowerCase();
    const terms = q.split(/\s+/).filter(Boolean);
    const cityQ = (city || "").trim().toLowerCase();

    let list = doctors.filter((d) => {
      if (cityQ) {
        const cityHit = (d.city || "").toLowerCase().includes(cityQ) || (d.location || "").toLowerCase().includes(cityQ);
        if (!cityHit) return false;
      }
      if (selectedSpec && d.specialtyKey !== selectedSpec) return false;

      if (!terms.length) return true;
      return terms.every((t) => d.searchCorpus.includes(t));
    });

    list = list.sort((a, b) => (b.rankScore ?? 0) - (a.rankScore ?? 0));
    return list;
  }, [doctors, debouncedWhat, city, selectedSpec]);

  // =========================
  // SEO (dynamic)
  // =========================


  function buildCanonical(baseUrl, { city, spec, q, lang }) {
    const p = new URLSearchParams();
    if (city) p.set("city", city.trim());
    if (spec) p.set("spec", spec.trim());
    if (q) p.set("q", q.trim());
    if (lang) p.set("lang", lang);

    const qs = p.toString();
    return qs ? `${baseUrl}?${qs}` : baseUrl;
  }

  const seo = useMemo(() => {
    const cityClean = (city || cityParam || "").trim();
    const specClean = (selectedSpec || specParam || "").trim(); // key
    const specObj = specialties.find((x) => x.key === specClean);
    const specLabel = specObj ? getSpecLabel(specObj, language) : specClean;

    const qClean = (what || qParam || "").trim();

    const baseName = "MikDok";
    const cityLabel = cityClean ? cityClean : language === "sq" ? "Shqipëri" : "Albania";

    let intentTitle = language === "sq" ? "Gjej doktorin e duhur" : "Find the right doctor";
    if (specClean && cityClean) intentTitle = language === "sq" ? `${specLabel} në ${cityClean}` : `${specLabel} in ${cityClean}`;
    else if (specClean) intentTitle = specLabel;
    else if (cityClean) intentTitle = language === "sq" ? `Doktorë në ${cityClean}` : `Doctors in ${cityClean}`;
    else if (qClean) intentTitle = qClean;

    const title = `${intentTitle} | ${baseName}`;

    const count = filteredDoctors.length;
    const descSq =
      specClean && cityClean
        ? `Gjej ${count} ${specLabel.toLowerCase()} në ${cityClean}. Rezervo online shpejt, shiko profilin, vlerësimet dhe takimet e lira.`
        : `Gjej profesionistë shëndeti në ${cityLabel}. Rezervo online shpejt, pa telefonata.`;

    const descEn =
      specClean && cityClean
        ? `Find ${count} ${specLabel.toLowerCase()} in ${cityClean}. Book online fast, view profiles, reviews, and next available slots.`
        : `Find healthcare professionals in ${cityLabel}. Book online fast — no calls.`;

    const description = language === "sq" ? descSq : descEn;

    const baseDomain = "https://mikdok.com";
    const baseUrl = "https://mikdok.com/";

    const usePretty = !!cityClean && !!specClean && !qClean;

    const canonicalSq = usePretty
      ? buildPrettyCanonical(baseDomain, { lang: "sq", city: cityClean, specKey: specClean })
      : buildCanonical(baseUrl, { city: cityClean, spec: specClean, q: qClean, lang: "sq" });

    const canonicalEn = usePretty
      ? buildPrettyCanonical(baseDomain, { lang: "en", city: cityClean, specKey: specClean })
      : buildCanonical(baseUrl, { city: cityClean, spec: specClean, q: qClean, lang: "en" });

    const canonical = language === "sq" ? canonicalSq : canonicalEn;

    const ogImage = "https://mikdok.com/og/cover.jpg";

    const itemListElement = filteredDoctors.slice(0, 50).map((d, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      url: `${baseUrl}doctor/${d.id}`,
      name: d.name,
    }));

    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      itemListOrder: "https://schema.org/ItemListOrderAscending",
      url: canonical,
      numberOfItems: itemListElement.length,
      itemListElement,
    };

    return { title, description, canonical, canonicalSq, canonicalEn, intentTitle, jsonLd, ogImage };
  }, [language, city, cityParam, selectedSpec, specParam, what, qParam, filteredDoctors, specialties]);

  useEffect(() => {
    document.title = seo.title;

    setOrCreateMeta("description", seo.description);
    setOrCreateMeta("robots", "index, follow");

    setCanonical(seo.canonical);
    setHreflangAlternates({ canonicalSq: seo.canonicalSq, canonicalEn: seo.canonicalEn });

    setOrCreateMeta("og:type", "website", true);
    setOrCreateMeta("og:site_name", "MikDok", true);
    setOrCreateMeta("og:title", seo.title, true);
    setOrCreateMeta("og:description", seo.description, true);
    setOrCreateMeta("og:url", seo.canonical, true);
    setOrCreateMeta("og:image", seo.ogImage, true);

    const locale = language === "sq" ? "sq_AL" : "en_GB";
    const altLocale = language === "sq" ? "en_GB" : "sq_AL";
    setOrCreateMeta("og:locale", locale, true);
    setOrCreateMeta("og:locale:alternate", altLocale, true);

    setOrCreateMeta("twitter:card", "summary_large_image", false);
    setOrCreateMeta("twitter:title", seo.title, false);
    setOrCreateMeta("twitter:description", seo.description, false);
    setOrCreateMeta("twitter:image", seo.ogImage, false);

    upsertJsonLd("doctor-list", seo.jsonLd);
  }, [seo, language]);

  const applySuggestion = (s) => {
    setShowSug(false);
    if (!s) return;

    if (s.type === "doctor" && s.doctorId) {
      navigate(`/doctor/${s.doctorId}`);
      return;
    }

    if (s.type === "specialty") {
      setSelectedSpec(s.specKey || s.value);
      setWhat("");
      document.getElementById("results")?.scrollIntoView({ behavior: "smooth" });
      return;
    }

    setSelectedSpec("");
    setWhat(s.value);
    document.getElementById("results")?.scrollIntoView({ behavior: "smooth" });
  };

  const onBook = () => {
    setIsLogin(true);
    setShowAuth(true);
  };

  const onViewProfile = (doctor) => {
    navigate(`/doctor/${doctor.id}`);
  };

  return (
    <div className="mdk-wrap">
      {/* Header */}
      <header className="mdk-header">
        <div className="mdk-container">
          <div className="mdk-header__inner">
            <div className="mdk-brand" onClick={() => navigate("/")}>
              <LogoMikDok showText={true} subtitle="" badge={56} size={34} markColor="#ffffff" />
            </div>

            <div className="mdk-actions">
              <button className="mdk-btn" onClick={toggleLanguage} type="button" aria-label="Toggle language">
                <span style={{ fontSize: 16, lineHeight: 1 }}>{languageFlag}</span>
                {!isMobile ? <b style={{ marginLeft: 6 }}>{language.toUpperCase()}</b> : null}
              </button>

              <button className="mdk-btn ghost" onClick={() => { setIsLogin(true); setShowAuth(true); }} type="button">
                <LogIn size={16} />
                {!isMobile ? t.login : null}
              </button>

              <button className="mdk-btn primary" onClick={() => { setIsLogin(false); setShowAuth(true); }} type="button">
                <UserPlus size={16} />
                {!isMobile ? t.signUp : null}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero + Search */}
      <section className="mdk-hero">
        <div className="mdk-container">
          <div className="mdk-hero-inner">
            <div className="mdk-hero-kicker">
              <i />
              {language === "sq" ? "Rezervo në sekonda • Pa telefonata" : "Book in seconds • No calls"}
            </div>

            <h1>
              {seo.intentTitle ? (
                <>
                  {seo.intentTitle.split(" ").slice(0, 1).join(" ")}{" "}
                  <span className="grad">{seo.intentTitle.split(" ").slice(1).join(" ")}</span>
                </>
              ) : (
                <>
                  {language === "sq" ? "Gjej " : "Find "}
                  <span className="grad">{language === "sq" ? "doktorin e duhur" : "the right doctor"}</span>.
                </>
              )}
            </h1>

            <p>{t.heroSubtitle}</p>

            <div className="mdk-hero-trust">
              <span className="mdk-trust-pill"><ShieldCheck size={14} /> {language === "sq" ? "Profesionistë të verifikuar" : "Verified professionals"}</span>
              <span className="mdk-trust-pill"><Clock size={14} /> {language === "sq" ? "Takime të shpejta" : "Fast bookings"}</span>
              <span className="mdk-trust-pill"><Star size={14} /> {language === "sq" ? "Vlerësime reale" : "Real reviews"}</span>
            </div>
            
          </div>

          <div className="mdk-search">
            <HomeNursingBanner
      language={language}
      waLink={waLink}
      images={nursingSlides}
      intervalMs={4500}
    />
            <div className="mdk-search__row">
              <div className="mdk-field" style={{ position: "relative" }} ref={sugRef}>
                <Search size={18} style={{ color: "rgba(11,15,24,.38)" }} />
                <input
                  value={what}
                  onChange={(e) => {
                    setWhat(e.target.value);
                    setShowSug(true);
                    setActiveSug(0);
                  }}
                  onFocus={() => {
                    setShowSug(true);
                    setActiveSug(0);
                  }}
                  onKeyDown={(e) => {
                    if (!showSug || suggestions.length === 0) return;

                    if (e.key === "ArrowDown") {
                      e.preventDefault();
                      setActiveSug((p) => Math.min(p + 1, suggestions.length - 1));
                    } else if (e.key === "ArrowUp") {
                      e.preventDefault();
                      setActiveSug((p) => Math.max(p - 1, 0));
                    } else if (e.key === "Enter") {
                      e.preventDefault();
                      applySuggestion(suggestions[activeSug]);
                    } else if (e.key === "Escape") {
                      setShowSug(false);
                    }
                  }}
                  placeholder={t.whatPlaceholder}
                  autoComplete="off"
                />

                {showSug && suggestions.length > 0 && (
                  <div className="mdk-suggest" role="listbox" aria-label="Search suggestions">
                    {suggestions.map((s, idx) => (
                      <button
                        key={`${s.type}-${s.value}-${idx}`}
                        type="button"
                        className={`mdk-suggest-item ${idx === activeSug ? "is-active" : ""}`}
                        onMouseDown={(ev) => ev.preventDefault()}
                        onClick={() => applySuggestion(s)}
                        role="option"
                        aria-selected={idx === activeSug}
                      >
                        <span className="mdk-suggest-chip" data-type={s.type}>
                          {s.label}
                        </span>
                        <span className="mdk-suggest-main">{s.value}</span>
                        {s.meta ? <span className="mdk-suggest-meta">{s.meta}</span> : <span />}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="mdk-field">
                <MapPin size={18} style={{ color: "rgba(11,15,24,.38)" }} />
                <input value={city} onChange={(e) => setCity(e.target.value)} placeholder={t.locationPlaceholder} />
              </div>

              <button
                className="mdk-search__cta"
                type="button"
                onClick={() => document.getElementById("results")?.scrollIntoView({ behavior: "smooth" })}
              >
                {t.searchBtn}
              </button>
            </div>

            <div className="mdk-search__row2">
              <div className="mdk-field">
                <Stethoscope size={18} style={{ color: "rgba(11,15,24,.38)" }} />
                <select value={selectedSpec} onChange={(e) => setSelectedSpec(e.target.value)} aria-label="Specialty">
                  <option value="">{t.anySpecialty}</option>
                  {specialtyCounts.map((s) => (
                    <option key={s.key} value={s.key}>
                      {s.label} ({s.count})
                    </option>
                  ))}
                </select>
              </div>

              <button
                className="mdk-search__cta"
                type="button"
                onClick={() => {
                  setWhat("");
                  setCity("");
                  setSelectedSpec("");
                  setShowSug(false);
                }}
              >
                {t.clearAll}
              </button>
            </div>
          </div>

          <div className="mdk-results-head" id="results">
            <div>
              <div className="mdk-results-title">{t.resultsTitle}</div>
              <div className="mdk-results-sub">
                {filteredDoctors.length} {filteredDoctors.length === 1 ? t.provider : t.providers}
              </div>
            </div>

            <div className="mdk-nav">
              <button className="mdk-btn" type="button" onClick={() => { setIsLogin(true); setShowAuth(true); }}>
                {t.login}
              </button>
              <button className="mdk-btn primary" type="button" onClick={() => { setIsLogin(false); setShowAuth(true); }}>
                {t.signUp}
              </button>
            </div>
          </div>
        </div>
      </section>

      <DoctorListSEO doctors={filteredDoctors} pageUrl={seo.canonical} />

      {/* Results */}
      <section>
        <div className="mdk-container">
          {loading && <div style={{ padding: "24px 0", color: "rgba(11,15,24,.55)", fontWeight: 900 }}>Loading…</div>}
          {error && <div style={{ padding: "16px 0", color: "#b91c1c", fontWeight: 900 }}>{error}</div>}

          {!loading && !error && (
            <>
              {filteredDoctors.length === 0 ? (
                <div style={{ padding: "18px 0", fontWeight: 900, color: "rgba(11,15,24,.55)" }}>
                  {t.noResults}
                </div>
              ) : (
                <div className="mdk-list">
                  {filteredDoctors.slice(0, 60).map((d) => (
                    <DoctorRow key={d.id} doctor={d} t={t} onBook={onBook} onView={() => onViewProfile(d)} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <SiteFooter t={t} />

      {showAuth && (
        <AuthSheet
          isLogin={isLogin}
          onClose={() => setShowAuth(false)}
          onSwitch={() => setIsLogin((p) => !p)}
          t={t}
          navigate={navigate}
        />
      )}
    </div>
  );
}
